import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, ExternalLink, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../utils/apiClient';

const AppEmbedOnboarding = () => {
  const [isActivated, setIsActivated] = useState(false);
  const [shopDomain, setShopDomain] = useState('');
  const [extensionApiKey, setExtensionApiKey] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    try {
      const response = await apiClient.get('/theme-extension/status');
      setIsActivated(response.data.is_activated);
      setShopDomain(response.data.shop_domain);
      setExtensionApiKey(response.data.extension_api_key);
    } catch (error) {
      console.error('Error loading onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateClick = () => {
    if (!shopDomain || !extensionApiKey) {
      console.error('Missing shop domain or extension API key');
      return;
    }

    // Construct the deep link to activate the app embed using the new api_key format
    const deepLink = `https://${shopDomain}/admin/themes/current/editor?context=apps&activateAppId=${extensionApiKey}/app-embed`;
    
    // Open in new window
    window.open(deepLink, '_blank');
    
    // Show a message to user
    toast.info('Theme Editor opened. Please toggle ON the Speakify Audio Player and click Save.');
  };

  const handleCheckStatus = async () => {
    setLoading(true);
    
    try {
      // Call the backend to mark onboarding as completed
      const response = await apiClient.post('/theme-extension/mark-completed');
      
      if (response.data.success) {
        setIsActivated(true);
        toast.success('Great! Audio player is now active on your store.');
      }
    } catch (error) {
      console.error('Error marking onboarding as completed:', error);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isActivated) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="w-6 h-6" />
            Audio Player is Active!
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-green-700 mb-4">
            Great! Your audio player is now active on your storefront. It will automatically appear on product pages that have audio descriptions.
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleCheckStatus}
              className="border-green-600 text-green-700 hover:bg-green-100"
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Refresh Status'}
            </Button>
            <Button 
              variant="ghost"
              onClick={handleActivateClick}
              className="text-green-700 hover:bg-green-100"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Theme Editor
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-blue-600" />
          Setup: Activate Audio Player on Your Store
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> To display audio players on your product pages, you need to activate the Speakify app embed in your theme.
          </AlertDescription>
        </Alert>

        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <h3 className="font-semibold text-sm">How it works:</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>Click the "Activate Audio Player" button below</li>
            <li>You'll be taken to your Theme Editor</li>
            <li>The Speakify audio player will be pre-selected</li>
            <li>Click "Save" in the top right corner</li>
            <li>Audio players will now appear on your product pages!</li>
          </ol>
        </div>

        <div className="flex gap-3">
          <Button 
            onClick={handleActivateClick}
            className="flex items-center gap-2"
            size="lg"
          >
            <ExternalLink className="w-4 h-4" />
            Activate Audio Player
          </Button>
          
          <Button 
            variant="outline"
            onClick={handleCheckStatus}
            disabled={loading}
          >
            {loading ? 'Checking...' : "I've activated it"}
          </Button>
        </div>

        <div className="pt-4 border-t">
          <details className="text-sm">
            <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
              Need help? View detailed instructions
            </summary>
            <div className="mt-3 space-y-2 text-gray-600">
              <p><strong>Step 1:</strong> Click "Activate Audio Player" above</p>
              <p><strong>Step 2:</strong> In the theme editor, you'll see "App embeds" in the left sidebar</p>
              <p><strong>Step 3:</strong> Find "Speakify Audio Player" and make sure it's toggled ON</p>
              <p><strong>Step 4:</strong> (Optional) Click on it to customize appearance and position</p>
              <p><strong>Step 5:</strong> Click the "Save" button in the top right</p>
              <p className="pt-2 text-blue-600">💡 The audio player will automatically appear on all product pages that have audio descriptions.</p>
            </div>
          </details>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppEmbedOnboarding;
